import { Injectable } from '@angular/core';
import { VacationSearchContent } from '@com-delta-idp/mnx-sitewide-header-ui';
import {
  Dates,
  NavSlimVacationCookieData,
  VacationSearchInput
} from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import {
  ADULTS_PLURAL_TEXT,
  ADULTS_SINGULAR_TEXT,
  CHILD_PLURAL_TEXT,
  CHILD_SINGULAR_TEXT,
  MODIFY_BUTTON,
  ROOM_PLURAL_TEXT,
  ROOM_SINGULAR_TEXT
} from '../../constants/header-slim/header-slim.constants';
import { getDateFormat } from '../../utils/header-slim/sanitization.utils';
import {
  emptyVacationCookieData,
  formatLabel,
  parsePassengers,
  parseRoute
} from '../../utils/vacation-header/vacation-header.utils';

@Injectable({ providedIn: 'root' })
export class VacationHeaderAdapter {
  transformFromCookie(raw: string): NavSlimVacationCookieData {
    // Format: "ATL - LAX/May 14 - 23/3 Adults, 3 Children"
    // or:     "LAX/May 14 - 23/1 Room, 3 Adults, 3 Children"
    const parts = raw.split('/');
    if (parts.length < 3) return emptyVacationCookieData();

    const [routePart, dates, passengerPart] = parts;
    const { origin, destination } = parseRoute(routePart.trim());
    const { rooms, adults, children } = parsePassengers(passengerPart.trim());

    return {
      origin,
      destination,
      dates: dates.trim(),
      rooms,
      adults,
      children
    };
  }

  transformFromInput(input: VacationSearchInput): NavSlimVacationCookieData {
    return {
      origin: input.origin,
      destination: input.destination,
      dates: this.formatDates(input.dates),
      rooms: formatLabel(input.rooms, ROOM_SINGULAR_TEXT, ROOM_PLURAL_TEXT),
      adults: formatLabel(
        input.adults,
        ADULTS_SINGULAR_TEXT,
        ADULTS_PLURAL_TEXT
      ),
      children: formatLabel(
        input.children,
        CHILD_SINGULAR_TEXT,
        CHILD_PLURAL_TEXT
      )
    };
  }

  toVacationSearchContent(
    data: NavSlimVacationCookieData
  ): VacationSearchContent {
    const passenger = [data.rooms, data.adults, data.children]
      .filter(Boolean)
      .join(', ');
    return {
      origin: data.origin,
      destination: data.destination,
      dates: data.dates,
      passenger,
      modifyButton: MODIFY_BUTTON
    };
  }

  toVacationSearchContentFromInput(
    input: VacationSearchInput
  ): VacationSearchContent {
    return this.toVacationSearchContent(this.transformFromInput(input));
  }

  private formatDates(dates: Dates): string {
    const departure = getDateFormat(dates.departureDate);
    if (!dates.returnDate) return departure;
    const returnFormatted = getDateFormat(dates.returnDate);
    const [depMonth] = departure.split(' ');
    const [, retDay] = returnFormatted.split(' ');
    return depMonth === returnFormatted.split(' ')[0]
      ? `${departure} - ${retDay}`
      : `${departure} - ${returnFormatted}`;
  }
}
